import { useCallback, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getLoginMethod, getSsoConfig } from '@api/sso';
import type { LoginMethodResponse } from '@api/types/auth';

const SSO_CONFIG_QUERY_KEY = ['sso-config'] as const;

export interface SsoRoute {
  provider: string;
  providerName: string;
}

export interface UseSsoRoutingResult {
  ssoRoute: SsoRoute | null;
  checkEmail: (email: string) => Promise<void>;
  resetSsoRoute: () => void;
}

function toRoute(method: LoginMethodResponse | null): SsoRoute | null {
  if (method?.method !== 'SSO' || !method.provider) return null;
  return { provider: method.provider, providerName: method.providerName ?? method.provider };
}

export function useSsoRouting(): UseSsoRoutingResult {
  const { data: config } = useQuery({
    queryKey: SSO_CONFIG_QUERY_KEY,
    queryFn: getSsoConfig,
    staleTime: Infinity,
  });
  const [ssoRoute, setSsoRoute] = useState<SsoRoute | null>(null);
  const checkedEmail = useRef<string | null>(null);

  const checkEmail = useCallback(
    async (email: string) => {
      const normalized = email.trim().toLowerCase();
      if (!config?.enabled || !normalized.includes('@') || normalized === checkedEmail.current) return;
      checkedEmail.current = normalized;
      const method = await getLoginMethod(normalized).catch(() => null);
      if (checkedEmail.current === normalized) setSsoRoute(toRoute(method));
    },
    [config?.enabled],
  );

  const resetSsoRoute = useCallback(() => {
    checkedEmail.current = null;
    setSsoRoute(null);
  }, []);

  return { ssoRoute, checkEmail, resetSsoRoute };
}

export default useSsoRouting;
